/**
 * FIX 4.6 — last-known SP cart totalQuantity for shell header badge.
 * SoT: session lastKnown helpers + cartStorage.loadCart — FORBID SalesPointContext.
 */

import { useSyncExternalStore } from 'react';
import { isPlatformChromeTenant } from './platformChromeTenant.js';
import {
  CUSTOMER_SHOP_CART_CHANGED_EVENT,
  type CustomerShopCartChangedDetail,
  loadCart,
} from '../features/shop/cart/cartStorage.js';
import {
  CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT,
  readLastKnownCartTenant,
  readLastKnownSalesPointId,
} from '../shared/session/lastKnownSalesPoint.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';

/**
 * F1 reader key for lastKnown / cart scope.
 * - Tenant chrome: URL tenant only (never a foreign tenant).
 * - Platform chrome: last commerce tenant from rememberSalesPoint* — never `__platform__`.
 */
export function resolveLastKnownCartReaderTenantCode(urlTenantCode: string): string | null {
  if (isPlatformChromeTenant(urlTenantCode)) {
    return readLastKnownCartTenant();
  }
  return urlTenantCode;
}

function readQuantity(readerTenantCode: string | null): number {
  if (readerTenantCode === null) {
    return 0;
  }
  const kioskId = readLastKnownSalesPointId(readerTenantCode);
  if (kioskId === null || kioskId <= 0) {
    return 0;
  }
  return loadCart(readerTenantCode, kioskId).totalQuantity;
}

/** @internal test helper — same SoT as badge hook without React subscription. */
export function readLastKnownShopCartQuantity(
  readerTenantCode: string | null,
): number {
  return readQuantity(readerTenantCode);
}

let cartChangedEpoch = 0;
const cartChangedListeners = new Set<() => void>();

function bumpCartChanged(): void {
  cartChangedEpoch += 1;
  for (const listener of cartChangedListeners) {
    listener();
  }
}

function subscribeCartChanged(onStoreChange: () => void): () => void {
  cartChangedListeners.add(onStoreChange);
  const onEvent = (_event: Event): void => {
    bumpCartChanged();
  };
  const onStorage = (event: StorageEvent): void => {
    if (event.storageArea !== window.localStorage) {
      return;
    }
    if (
      event.key === null ||
      event.key.startsWith('rpapp-customer.shop.cart:')
    ) {
      bumpCartChanged();
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener(CUSTOMER_SHOP_CART_CHANGED_EVENT, onEvent);
    window.addEventListener(CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT, onEvent);
    window.addEventListener('storage', onStorage);
  }
  return () => {
    cartChangedListeners.delete(onStoreChange);
    if (typeof window !== 'undefined') {
      window.removeEventListener(CUSTOMER_SHOP_CART_CHANGED_EVENT, onEvent);
      window.removeEventListener(
        CUSTOMER_LAST_KNOWN_SALES_POINT_CHANGED_EVENT,
        onEvent,
      );
      window.removeEventListener('storage', onStorage);
    }
  };
}

function getCartChangedSnapshot(): number {
  return cartChangedEpoch;
}

/** Compact badge label — reuse totalQuantity; cap display at 99+. */
export function formatCartBadgeQuantity(totalQuantity: number): string {
  if (totalQuantity > 99) {
    return '99+';
  }
  return String(totalQuantity);
}

/**
 * totalQuantity for the last-known sales-point cart only (not sum across SPs).
 */
export function useLastKnownShopCartQuantity(): number {
  const urlTenantCode = useTenantCode();
  const epoch = useSyncExternalStore(
    subscribeCartChanged,
    getCartChangedSnapshot,
    () => 0,
  );
  void epoch;
  const readerKey = resolveLastKnownCartReaderTenantCode(urlTenantCode);
  return readQuantity(readerKey);
}

/** @internal test helper */
export function __resetLastKnownShopCartQuantityStoreForTests(): void {
  cartChangedEpoch = 0;
  cartChangedListeners.clear();
}

export type { CustomerShopCartChangedDetail };
